import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import SafeIcon from '../../common/SafeIcon';
import Layout from '../Layout';
import * as FiIcons from 'react-icons/fi';

const { FiMusic, FiUsers, FiAward, FiSliders, FiActivity, FiClock } = FiIcons;

const FeaturesPage = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  // Direct inline style for title color
  const titleStyle = {
    fontWeight: 700,
    color: isDark ? '#0ea5e9' : '#6b21a8' // sky-500 for dark, purple-800 for light
  };

  return (
    <Layout>
      <div className="space-y-6">
        <Helmet>
          <title>{language === 'zh' ? '功能介紹 | Beat Buddy 節拍器' : 'Features | Beat Buddy Metronome'}</title>
          <meta name="description" content={language === 'zh' ? "探索 Beat Buddy 節拍器的所有功能：互動角色、成就系統、自定義設置等。了解這款創新節拍器如何讓音樂練習變得更有趣和高效。" : "Explore all features of Beat Buddy metronome: interactive characters, achievement system, custom settings and more. Learn how this innovative metronome makes music practice fun and effective."} />
          <meta name="keywords" content="Beat Buddy, metronome features, music practice tools, interactive metronome, rhythm training, music learning app" />
          <link rel="canonical" href="https://beatbuddy.app/features" />
        </Helmet>

        <div className="text-center max-w-xl mx-auto">
          <h2 className="text-3xl mb-2">
            <span style={titleStyle}>{t('featuresTitle')}</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mx-auto">
            {t('featuresSubtitle')}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-center text-purple-600 dark:text-purple-400">
              {t('featuresIntroTitle')}
            </h3>
            <p className="text-center text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              {t('featuresIntroText')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:scale-105">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 flex items-center justify-center mb-4">
                <SafeIcon icon={FiMusic} className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">{t('featureMetronomeTitle')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('featureMetronomeDesc')}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  {t('featureMetronomeBullet1')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  {t('featureMetronomeBullet2')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  {t('featureMetronomeBullet3')}
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:scale-105">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-400 to-cyan-400 flex items-center justify-center mb-4">
                <SafeIcon icon={FiUsers} className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">{t('featureCharactersTitle')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('featureCharactersDesc')}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  {t('featureCharactersBullet1')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  {t('featureCharactersBullet2')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  {t('featureCharactersBullet3')}
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:scale-105">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 flex items-center justify-center mb-4">
                <SafeIcon icon={FiAward} className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">{t('featureAchievementsTitle')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('featureAchievementsDesc')}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                  {t('featureAchievementsBullet1')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                  {t('featureAchievementsBullet2')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                  {t('featureAchievementsBullet3')}
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:scale-105">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 flex items-center justify-center mb-4">
                <SafeIcon icon={FiSliders} className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">{t('featureCustomizationTitle')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('featureCustomizationDesc')}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  {t('featureCustomizationBullet1')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  {t('featureCustomizationBullet2')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  {t('featureCustomizationBullet3')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  {t('featureCustomizationBullet4')}
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:scale-105">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-rose-400 to-red-400 flex items-center justify-center mb-4">
                <SafeIcon icon={FiActivity} className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">{t('featureStatsTitle')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('featureStatsDesc')}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-2"></span>
                  {t('featureStatsBullet1')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-2"></span>
                  {t('featureStatsBullet2')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-2"></span>
                  {t('featureStatsBullet3')}
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:scale-105">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-indigo-400 to-violet-400 flex items-center justify-center mb-4">
                <SafeIcon icon={FiClock} className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">{t('featureProToolsTitle')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('featureProToolsDesc')}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                  {t('featureProToolsBullet1')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                  {t('featureProToolsBullet2')}
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                  {t('featureProToolsBullet3')}
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12">
            <h3 className="text-xl font-semibold mb-6 text-center text-purple-600 dark:text-purple-400">
              {t('featureWhyTitle')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-xl p-6 text-center">
                <div className="text-4xl mb-4">🎵</div>
                <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">{t('featureFunTitle')}</h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('featureFunDesc')}
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/30 dark:to-green-900/30 rounded-xl p-6 text-center">
                <div className="text-4xl mb-4">⚡</div>
                <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">{t('featureEfficiencyTitle')}</h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('featureEfficiencyDesc')}
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-green-50 to-amber-50 dark:from-green-900/30 dark:to-amber-900/30 rounded-xl p-6 text-center">
                <div className="text-4xl mb-4">🚀</div>
                <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">{t('featureProgressTitle')}</h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('featureProgressDesc')}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <a href="#" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold hover:scale-105 transition-transform duration-200">
              {t('featureStartNow')}
            </a>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
              {t('freeNoSignup')}
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FeaturesPage;